import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, UpdateSettingsInput } from '@rw/shared';
import { useEffect } from 'react';
import { Card } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { toast } from '../../stores/toasts';
import { LANGS, useLang, useT } from '../../i18n';

export function SettingsCard() {
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const online = useNetwork((s) => s.online);
  const qc = useQueryClient();
  const t = useT();

  // Refresh the cached profile (older sessions may predate settings).
  const me = useQuery({ queryKey: ['me', 'profile'], queryFn: () => api<MeResponse>('/auth/me') });
  useEffect(() => {
    if (me.data) setUser(me.data);
  }, [me.data, setUser]);

  const update = useMutation({
    mutationFn: (body: UpdateSettingsInput) => api<MeResponse>('/me/settings', { method: 'PATCH', body }),
    onSuccess: (u) => {
      // Keep the (persisted) profile query in sync, or a reload would restore the old value.
      qc.setQueryData(['me', 'profile'], u);
      setUser(u);
    },
    onError: (e) => toast({ kind: 'error', title: t('Could not save setting'), body: e instanceof ApiError ? e.message : undefined }),
  });

  const hide = update.isPending ? !!update.variables?.hideRouteEnds : !!user?.settings?.hideRouteEnds;

  return (
    <Card>
      <Toggle
        id="hide-route-ends"
        checked={hide}
        disabled={!online || update.isPending || !user?.settings}
        onChange={(v) => update.mutate({ hideRouteEnds: v })}
        label={t('Hide start & end of my routes')}
        description={t('Other runners won’t see the first and last 200 m of your runs (protects your home). You always see your full route.')}
      />
    </Card>
  );
}

/** English / Tiếng Việt — stored on this device. */
export function LanguageCard() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  return (
    <Card>
      <div className="font-semibold">{t('Language')}</div>
      <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('Language')}>
        {LANGS.map((l) => (
          <button
            key={l.code}
            role="radio"
            aria-checked={lang === l.code}
            onClick={() => setLang(l.code)}
            data-testid={`lang-${l.code}`}
            className={`h-11 rounded-xl border font-semibold transition ${lang === l.code ? 'border-volt bg-volt/10 text-volt' : 'border-ink-700 text-ink-300'}`}
          >
            {l.label}
          </button>
        ))}
      </div>
    </Card>
  );
}

export function Toggle({
  id,
  checked,
  disabled,
  onChange,
  label,
  description,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex-1">
        <label htmlFor={id} className="font-semibold">
          {label}
        </label>
        <p className="mt-0.5 text-xs text-ink-400">{description}</p>
      </div>
      <button
        id={id}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        data-testid={id}
        onClick={() => onChange(!checked)}
        className={`relative mt-1 h-7 w-12 shrink-0 rounded-full transition disabled:opacity-50 ${checked ? 'bg-volt' : 'bg-ink-600'}`}
      >
        <span className={`absolute top-0.5 size-6 rounded-full bg-white shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
      </button>
    </div>
  );
}
